import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShowcase } from '../../components/AuthShowcase.js';
import { AuthLayout, Field, FormError } from '../../components/Form.js';
import { LegalLinks } from '../../components/LegalLinks.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { GoogleButton } from './GoogleButton.js';

/** Story #3 — sign in. */
export function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await signIn(email, password);
      // Only same-origin relative paths, so `next` cannot bounce to another site.
      const next = params.get('next');
      navigate(next?.startsWith('/') && !next.startsWith('//') ? next : '/polls');
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'email_unverified') {
        navigate(`/verify?email=${encodeURIComponent(email)}`);
        return;
      }
      setError(caught instanceof ApiError ? caught.message : 'Could not sign you in.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      aside={<AuthShowcase />}
      footer={
        <>
          <p className="muted">
            New here? <Link to="/signup">Create an account</Link>
          </p>
          <LegalLinks />
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <Field label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </Field>

        <Field label="Password" htmlFor="password">
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>

        <FormError message={error} />

        <button type="submit" disabled={busy} className="block">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="muted center">
          <Link to="/forgot-password">Forgot your password?</Link>
        </p>
      </form>

      <GoogleButton label="Sign in with Google" />
    </AuthLayout>
  );
}
